import { z } from 'zod'
import { type BelangSource, belangDossierveldenOf } from './belangenverklaring.js'
import { Block, PublicatieId, RondeSleutel, UitvraagRef } from './id.js'
import { Moment } from './moment.js'
import { Text } from './text.js'

/**
 * De draaiende publicaties voor het proefprocessysteem (`GET /v1/proefpublicaties`, besluit van de product
 * owner van 2 november 2026).
 *
 * Het proefprocessysteem is testgereedschap buiten het product (`apps/processysteem-simulatie`, "Het
 * proefprocessysteem"). Een tester koos daar een formulier door het adres van de pod te plakken; met deze
 * lijst kiest hij uit wat er draait. Het koppelvlak verandert daar niet door: een processysteem van buiten
 * krijgt deze lijst niet, alleen de aanroeper `proefprocessysteem` van de opslagdienst, binnen het cluster.
 *
 * **Wat erin staat en wat niet.** Genoeg om een formulier te herkennen en zijn pod te vinden: de titel in
 * beide talen, het blok, de uitvraag, de ronde, het adres en of het een toetslaag heeft. Niet de bevroren
 * definitie, niet wie publiceerde en niets van een dossier: de tester heeft dat niet nodig om te kiezen.
 *
 * Een eigen vorm en geen veld op `PodStateEntry` of `PodOverviewEntry`: die lezen de configurator en de
 * scripts strikt.
 */
export const RunningPublicatieEntry = z.strictObject({
  publicatieId: PublicatieId,
  blok: Block,
  uitvraagRef: UitvraagRef,
  /** De titel van de bevroren definitie, in beide talen. */
  titel: Text,
  /** Het adres van de pod; ontbreekt bij een publicatie van vóór migratie 0016. */
  adres: z.url({ protocol: /^https$/ }).optional(),
  ronde: RondeSleutel,
  /** Of het formulier een toetslaag heeft (`heeftToetslaag` in `definitie.ts`, besluit van 2 november 2026). */
  toetslaag: z.boolean(),
  gepubliceerdOp: Moment,
  /**
   * De velden van het dossier-contract die dit formulier bij het uitzetten aanneemt (`dossierveldenOf`),
   * gesorteerd. Het proefprocessysteem geeft alleen deze mee; een ander veld weigert de opslagdienst met
   * `dossier-contract-past-niet` (`docs/koppelvlak.md` hoofdstuk 4). Sinds 3 november 2026, additief; een
   * opslagdienst van daarvoor levert het niet, en dan geeft het proefprocessysteem geen enkel veld mee.
   */
  dossiervelden: z.array(z.string().min(1)).optional(),
})
export type RunningPublicatieEntry = z.infer<typeof RunningPublicatieEntry>

/** Een veld zoals `dossierveldenOf` het leest: alleen de naam van het veld uit het dossier-contract. */
interface ShownField {
  readonly dossierveld?: { readonly naam: string } | undefined
}

/**
 * De namen in het dossier-contract die een formulier aanneemt: elk veld dat een veld van het processysteem
 * toont (R-54, R-56), ook in een herhaalgroep, plus wat de belangenverklaring uit het dossier leest
 * (`belangDossierveldenOf`, R-184i). Dezelfde twee bronnen als de toets in de opslagdienst
 * (`checkDossierFields` in `apps/store/src/dossierFields.ts`); de test daar houdt ze gelijk.
 */
export function dossierveldenOf(
  definitie: Pick<BelangSource, 'advies' | 'belangenverklaring'> & { readonly secties: readonly { readonly velden: readonly (ShownField & { readonly rijVelden?: readonly ShownField[] | undefined })[] }[] },
): string[] {
  const names = new Set<string>()
  for (const section of definitie.secties) {
    for (const field of section.velden) {
      for (const shown of [field, ...(field.rijVelden ?? [])]) if (shown.dossierveld !== undefined) names.add(shown.dossierveld.naam)
    }
  }
  for (const name of belangDossierveldenOf(definitie)) names.add(name)
  return [...names].sort()
}

/** Het antwoord van `GET /v1/proefpublicaties`: oudste eerst, alleen publicaties waarvan de pod draait. */
export const RunningPublicatieList = z.strictObject({
  publicaties: z.array(RunningPublicatieEntry),
})
export type RunningPublicatieList = z.infer<typeof RunningPublicatieList>
